import CameraTile, { type CameraTileSource } from "./CameraTile";

// ===========================================================================
// CameraGrid — mosaico 1 / 4 / 8 / 12 al estilo iVMS-4200.
//   1  → 1×1          4 → 2×2
//   8  → 4×4 con una celda grande de 3×3 y 7 chicas alrededor
//   12 → 4×3
// La celda maximizada (doble clic) ocupa todo el mosaico.
// ===========================================================================

export const GRID_LAYOUTS = [1, 4, 8, 12] as const;
export type GridLayout = (typeof GRID_LAYOUTS)[number];

const GRID_CLASSES = {
  1: "grid-cols-1 grid-rows-1",
  4: "grid-cols-2 grid-rows-2",
  8: "grid-cols-4 grid-rows-4",
  12: "grid-cols-4 grid-rows-3",
} as const satisfies Record<GridLayout, string>;

export interface CameraGridProps {
  layout: GridLayout;
  /** Una cámara (o vacío) por celda; se recorta o se rellena al tamaño del mosaico. */
  slots: (CameraTileSource | null)[];
  selectedIndex?: number | null;
  maximizedIndex?: number | null;
  onSelect?: (index: number) => void;
  onToggleMaximize?: (index: number) => void;
  onDropCamera?: (index: number, cameraId: string) => void;
  onClear?: (index: number) => void;
  className?: string;
}

export default function CameraGrid({
  layout,
  slots,
  selectedIndex = null,
  maximizedIndex = null,
  onSelect,
  onToggleMaximize,
  onDropCamera,
  onClear,
  className = "",
}: CameraGridProps) {
  const cells = Array.from({ length: layout }, (_, i) => slots[i] ?? null);

  if (maximizedIndex !== null && maximizedIndex < layout) {
    const cam = cells[maximizedIndex];
    return (
      <div className={`grid grid-cols-1 grid-rows-1 gap-px bg-black ${className}`} data-testid="camera-grid" data-layout="max">
        <CameraTile
          camera={cam}
          hd
          index={maximizedIndex}
          selected
          maximized
          onToggleMaximize={() => onToggleMaximize?.(maximizedIndex)}
          onDropCamera={onDropCamera ? (id) => onDropCamera(maximizedIndex, id) : undefined}
        />
      </div>
    );
  }

  return (
    <div className={`grid gap-px bg-neutral-800 ${GRID_CLASSES[layout]} ${className}`} data-testid="camera-grid" data-layout={layout}>
      {cells.map((cam, i) => {
        const big = layout === 8 && i === 0;
        return (
          <CameraTile
            key={`${i}-${cam?.id ?? "empty"}`}
            camera={cam}
            hd={layout <= 4 || big}
            compact={layout === 12 || (layout === 8 && !big)}
            index={i}
            selected={selectedIndex === i}
            className={big ? "col-span-3 row-span-3" : ""}
            onSelect={() => onSelect?.(i)}
            onToggleMaximize={() => onToggleMaximize?.(i)}
            onDropCamera={onDropCamera ? (id) => onDropCamera(i, id) : undefined}
            onClear={onClear && cam ? () => onClear(i) : undefined}
          />
        );
      })}
    </div>
  );
}
